/**
 * 标已读（`POST /communicator/{pet_id}/read`，A 2026-09-27 落；调用口径 A、I 定）：
 * - 最新那条**真正显示到屏幕上**才算看到：消息区停在最底下、页面在前台（ChatScroller 的 onSeenLatest 报过来）；
 *   往上翻更早的页、停在历史里来新消息、页面在后台、在“TA 的朋友”分区，都不算，不发。
 * - read_through 原样用那条消息的 available_at **字符串**，不经过 Date（库里到微秒，Date 只到毫秒，截掉会让最后那条一直算未读）。
 * - 哪条最新按列表顺序（服务端给的顺序）认，不比时间串（整秒的串没有小数部分，按字符串比会排错）。
 * - 同一条只发一次；每次新发一个幂等键（同键配不同 read_through 服务端回 409）。失败不重试，下一次看到最新一条时再发。
 * - 页面在后台时看到的先记着，回前台再补发一次。
 * - 回包的 unread 与首页快照 unread.messages 是同一个数：直接写进首页缓存（不另请求一次 /home）；回包带了两个分区各自的未读
 *   （unread_private、unread_family）就一起写进 messages_private、messages_family（分区按钮上的小点跟着变）。
 * - 带上分区 channel（R26 第 1 条，I 10-03 02:1x 同意签名）：只推进屏幕上这个分区的已读位置。不带时后端两个分区一起推进，
 *   在“我和 TA”看到最新一条会把还没看过、时间更早的家庭来信也算成已读。
 */
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { HomeSnapshot, MessageChannel, MessageSummary } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";

const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

export function useMarkRead({ userId, petId, channel, enabled }: { userId: string | null; petId: string | null | undefined; channel?: MessageChannel; enabled: boolean }) {
  const { communicator } = useServices();
  const queryClient = useQueryClient();
  // 分区也算进去：换了分区，上一个分区记着的那条不在这里补发
  const scope = `${userId ?? "-"}|${petId ?? "-"}|${channel ?? "-"}`;
  /** 这个账号 + 宠物已经发过的那条（按 message_id 去重）；换账号或换宠物就不算 */
  const sent = useRef<{ scope: string; messageId: string } | null>(null);
  /** 在后台时看到的最新一条（连同是哪个账号 + 宠物）：回前台补发；换了宠物就作废，不把上一只的那条记到这一只头上 */
  const pending = useRef<{ scope: string; message: MessageSummary } | null>(null);

  const mark = useCallback(
    (m: MessageSummary) => {
      if (!enabled || !petId || !m.available_at) return;
      if (!visible()) {
        pending.current = { scope, message: m };
        return;
      }
      if (sent.current?.scope === scope && sent.current.messageId === m.message_id) return;
      sent.current = { scope, messageId: m.message_id };
      pending.current = null;
      const readThrough = m.available_at;
      // 包进 Promise：服务同步抛错（比如测试垫片里没有这个方法）也按“没发成”处理，不打断摆位置的 effect
      Promise.resolve()
        .then(() => communicator.markRead(petId, readThrough, newIdempotencyKey("mark-read"), channel))
        .then((result) => {
          const parts = {
            ...(typeof result.unread_private === "number" ? { messages_private: result.unread_private } : {}),
            ...(typeof result.unread_family === "number" ? { messages_family: result.unread_family } : {}),
          };
          queryClient.setQueryData<HomeSnapshot>(queryKeys.homeFor(userId ?? "-", petId), (old) =>
            old ? { ...old, unread: { ...old.unread, messages: result.unread, ...parts } } : old,
          );
        })
        .catch(() => {
          // 没发成：下一次看到最新一条时再发（不自动重试、不打扰）
          if (sent.current?.scope === scope && sent.current.messageId === m.message_id) sent.current = null;
        });
    },
    [channel, communicator, enabled, petId, queryClient, scope, userId],
  );

  useEffect(() => {
    const onVisible = () => {
      const waiting = pending.current;
      if (visible() && waiting && waiting.scope === scope) mark(waiting.message);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [mark, scope]);

  return mark;
}
